import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Divider, Form, Radio, Row, Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs, { Dayjs } from "dayjs";

import Text, { type colorTypes } from "../../../components/Text/Text";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import { defaultPropsFormRequire, formStyle } from "../../../utility/DefaultProps";
import Select from "../../../components/Select/Select";
import DatePicker from "../../../components/DatePicker/DatePicker";
import TextArea from "../../../components/Input/TextArea";
import { NumberNotZero } from "../../../utility/validator";
import ConfirmModal from "../../../components/Modal/ConfirmModal";
import DetailSection from "./DetailSection";
import { getCustomerId } from "../../../datasource/CustomerDatasource";
import {
  createTempCreditLimit,
  getTempCreditLimitDetail,
  updateTempCreditLimit,
} from "../../../datasource/CreditLimitDatasource";
import { CardContainer } from "../../../components/Card/CardContainer";
import PageTitleNested from "../../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../../components/BreadCrumb/BreadCrumb";
import moment from "moment";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";
import ApprovalSection from "../../CreditLimitPage/Section/ApprovalSection";
import { MainOrderCreditDetailTabNew } from "../../CreditLimitPage/DetailTabs/MainNew";

const StyledForm = styled(Form)`
  ${formStyle}
`;

function RequestCredit() {
  const { pathname } = window.location;
  const pathSplit = pathname.split("/");
  const isEdit = pathSplit[3] === "edit";
  const [cusCompId, setCusCompId] = useState<string>("");
  const companyCode = JSON.parse(localStorage.getItem("company")!);
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>();
  const [shopData, setShopData] = useState<{
    customerCompanyId: string;
    dealerCode: string;
    customerId: string;
    dealerName: string;
    dealerNo: string;
  } | null>(null);
  const [customerComp, setCustomerComp] = useState<any>();
  const [enableInput, setEnableInput] = useState(false);
  const [beforeStatus, setBeforeStatus] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [hasFile, setHasFile] = useState<any>();
  const [cusId, setCusId] = useState<string>("");
  const location = useLocation();
  const tempId = location.state?.tempId;

  useEffect(() => {
    const fetchCreditLimitDetail = async () => {
      if (!tempId) return;

      try {
        const res = await getTempCreditLimitDetail(tempId);
        setCusCompId(res.customerCompanyId);
        setEnableInput(res.status === "APPROVED");
        setSelectedStatus(res.status);
        setBeforeStatus(res.status);
        if (res.status === "APPROVED") {
          form.setFieldValue("file", res.pathFile);
        }
        form.setFieldsValue({
          remarkRequest: res.remarkRequest,
          creditLimitRequest: res.creditLimitRequest,
          startDate: dayjs(res.startDate),
          endDate: dayjs(res.endDate),
          userRequest: res.userRequest,
          status: res.status,
          isActive: res.isActive,
          remarkReject: res.remarkReject,
        });
      } catch (error) {
        console.log(error);
      }
    };

    if (!isEdit) {
      setCusCompId(tempId);
    } else {
      fetchCreditLimitDetail();
    }
  }, [tempId]);

  useEffect(() => {
    const fetchCustomerData = async () => {
      const customerId = tempId ? cusId : (shopData?.customerId ?? cusCompId);
      if (!customerId) return;

      try {
        const res = await getCustomerId({
          customerId,
          company: companyCode.companyCode,
        });

        setCustomerComp(
          res?.customerToUserShops?.map((item) => ({
            cusCompName: `${item?.userShop?.firstname ?? ""} ${
              item?.userShop?.lastname ?? ""
            }`.trim(),
          })) ?? [],
        );
      } catch (error) {
        console.error("Error fetching customer data:", error);
      }
    };

    fetchCustomerData();
  }, [shopData?.customerId, cusId]);

  useEffect(() => {
    if (customerComp?.length === 1) {
      form.setFieldsValue({ userRequest: customerComp[0].cusCompName });
    }
  }, [customerComp]);

  useEffect(() => {
    form.resetFields();
  }, [shopData?.customerId, tempId]);

  const onFinish = async (values) => {
    const userFullName = `${userProfile?.firstname} ${userProfile?.lastname}`;
    const formatDate = (date, hours, minutes, seconds, ms) => {
      const d = new Date(date);
      d.setHours(hours, minutes, seconds, ms);
      return d.toISOString();
    };
    // create แบบเลือกร้านใช้ shopData?.customerCompanyId
    // เลือกร้านมา create จาก tab ทั้งหมด (หรือ edit) ใช้ cusCompId
    const conditionCreate = tempId ? cusCompId : shopData?.customerCompanyId;
    const commonData = {
      customerCompanyId: conditionCreate,
      company: companyCode?.companyCode,
      creditLimitRequest: values.creditLimitRequest || "",
      startDate: formatDate(values.startDate, 0, 0, 0, 0),
      endDate: formatDate(values.endDate, 23, 59, 59, 999),
      userRequest: values.userRequest || "",
      remarkRequest: values.remarkRequest || "",
      updatedBy: userFullName,
    };

    try {
      if (tempId) {
        const formData = new FormData();
        Object.entries({
          ...commonData,
          temporaryCreditLimitId: tempId || "",
          status: selectedStatus || "",
          remarkReject: values.remarkReject || "",
          reason: reason || "",
          isActive: values.isActive === "false" ? false : Boolean(values.isActive),
        }).forEach(([key, val]) => formData.append(key, val));

        if (values.file) formData.append("file", values.file);
        await updateTempCreditLimit(formData);
      } else {
        await createTempCreditLimit({ ...commonData, createdBy: userFullName });
      }
      navigate(-1);
    } catch (error) {
      console.error("Error processing credit limit request:", error);
    }
  };

  const handleFormChange = (changedValues: any, allValues: any) => {
    const status = form.getFieldValue("status");
    const remarkReject = form.getFieldValue("remarkReject");
    let isValid = true;
    if (status === "REJECTED" && !remarkReject) {
      isValid = false;
    }
    if (status === "WAITING") {
      isValid = true;
    }
    setIsFormValid(isValid);
  };

  useEffect(() => {
    handleFormChange({}, form.getFieldsValue(true));
  }, [hasFile]);
  const normalizedStatus = normalizeStatus(selectedStatus);
  const meta = STATUS_CREDIT[normalizedStatus] || { label: "-", color: "gray" };
  return (
    <CardContainer>
      <PageTitleNested
        title={`${tempId ? "แก้ไข" : ""}คำขอวงเงินชั่วคราว`}
        showBack
        onBack={() => navigate(-1)}
        extra={
          tempId ? (
            <Space direction='vertical' size={1} style={{ display: "flex", alignItems: "end" }}>
              <Text
                fontSize={20}
                fontWeight={700}
                style={{
                  color: meta.color,
                  fontWeight: 700,
                  marginRight: 12,
                }}
              >
                {meta.label}
              </Text>
              <Text fontFamily='Sarabun' fontSize={14}>
                {moment(form.getFieldValue("updatedAt")).format("DD/MM/YYYY, HH:mm")}
              </Text>
            </Space>
          ) : undefined
        }
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "เช็คคำขอวงเงิน", path: -1 },
              { text: `${tempId ? "แก้ไข" : ""}คำขอวงเงินชั่วคราว`, path: "" },
            ]}
          />
        }
      />
      <Divider style={{ marginTop: "2%" }} />
      <DetailSection
        temporaryCreditLimitId={tempId}
        onCallbackShop={(shop) => setShopData(shop)}
        cusCompId={cusCompId}
        callBackCusId={(cusId) => setCusId(cusId)}
      />
      <StyledForm
        {...defaultPropsFormRequire}
        style={{
          paddingTop: 18,
        }}
        form={form}
        onFinish={onFinish}
        onValuesChange={handleFormChange}
      >
        {(shopData?.customerId || tempId) && (
          <>
            <Row gutter={16} justify='space-between' style={{ marginTop: 16 }}>
              <Col span={12}>
                <Form.Item
                  label='วงเงินร้านค้าที่ขอ'
                  name='creditLimitRequest'
                  rules={[
                    {
                      required: true,
                      message: "กรุณากรอกวงเงินร้านค้าที่ขอ",
                    },
                  ]}
                >
                  <Input
                    disabled={enableInput}
                    placeholder='0.00'
                    autoComplete='off'
                    onKeyDown={NumberNotZero}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='ผู้ดำเนินการขอ'
                  name='userRequest'
                  rules={[
                    {
                      required: true,
                      message: "กรุณาเลือกชื่อผู้ดำเนินการขอ",
                    },
                  ]}
                >
                  <Select
                    disabled={enableInput}
                    style={{ marginBottom: 8 }}
                    data={customerComp?.map((values: any, index: number) => ({
                      key: index,
                      label: values?.cusCompName,
                      value: values?.cusCompName,
                    }))}
                    placeholder='ค้นหาชื่อผู้ดำเนินการขอ'
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row
              gutter={4}
              align='middle'
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Form.Item
                label='วันที่เริ่มต้น'
                name='startDate'
                style={{ flex: 1 }}
                rules={[
                  {
                    required: true,
                    message: "กรุณาเลือกวันที่เริ่มต้น",
                  },
                ]}
              >
                <DatePicker
                  suffixIcon={<CalendarOutlined />}
                  style={{ width: "100%" }}
                  disabled={enableInput}
                  disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
                  onChange={(date: Dayjs) => {
                    const endDate = form.getFieldValue("endDate");
                    if (date && endDate && date.isAfter(endDate)) {
                      form.setFieldsValue({ endDate: null });
                    }
                  }}
                />
              </Form.Item>
              <div style={{ textAlign: "center" }}>-</div>
              <Form.Item
                label='วันที่สิ้นสุด'
                name='endDate'
                style={{ flex: 1 }}
                rules={[
                  {
                    required: true,
                    message: "กรุณาเลือกวันที่สิ้นสุด",
                  },
                ]}
              >
                <DatePicker
                  suffixIcon={<CalendarOutlined />}
                  style={{ width: "100%" }}
                  disabledDate={(current: Dayjs) => {
                    const startDate = form.getFieldValue("startDate");
                    return current && current.isBefore(dayjs(startDate));
                  }}
                  enablePast
                />
              </Form.Item>
            </Row>
            <Row gutter={16} justify='space-between' style={{ marginTop: 16 }}>
              <Col span={24}>
                <Form.Item
                  label='เหตุผลการขอ'
                  name='remarkRequest'
                  rules={[
                    {
                      required: true,
                      message: "กรุณากรอกเหตุผลการขอ",
                    },
                  ]}
                >
                  <TextArea placeholder='ระบุเหตุผลการขอ' />
                </Form.Item>
              </Col>
            </Row>
            {isEdit && (
              <Form.Item
                name='status'
                label='สถานะ'
                rules={[
                  {
                    required: true,
                    message: "*โปรดระบุสถานะ",
                  },
                ]}
              >
                <Radio.Group
                  style={{ width: "100%" }}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  value={selectedStatus}
                >
                  <Space direction='vertical' style={{ width: "100%" }}>
                    {[
                      { value: "WAITING", label: "รออนุมัติ" },
                      { value: "APPROVED", label: "อนุมัติแล้ว" },
                      { value: "REJECTED", label: "ไม่อนุมัติ" },
                    ].map(({ value, label }) => (
                      <div key={value}>
                        <Radio value={value}>
                          <Text fontFamily='Sarabun' fontSize={14}>
                            {label}
                          </Text>
                        </Radio>

                        {value === "APPROVED" && selectedStatus === "APPROVED" && (
                          <ApprovalSection
                            form={form}
                            afterStatus={beforeStatus}
                            onFileUpload={(file) => setHasFile(file)}
                          />
                        )}

                        {value === "REJECTED" && selectedStatus === "REJECTED" && (
                          <Form.Item
                            style={{ marginLeft: "24px" }}
                            label='เหตุผล'
                            name='remarkReject'
                            rules={[{ required: true, message: "กรุณาระบุเหตุผล" }]}
                          >
                            <TextArea placeholder='ระบุเหตุผล' />
                          </Form.Item>
                        )}
                      </div>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
            )}
          </>
        )}
        <Divider style={{ marginTop: "4%" }} />
        <Row justify='space-between' gutter={12}>
          <Col xl={3} sm={6}>
            <Button typeButton='primary-light' title='ย้อนกลับ' onClick={() => navigate(-1)} />
          </Col>
          <Col xl={15} sm={6}></Col>
          <Col xl={3} sm={6}>
            <Button
              typeButton={!isFormValid || !cusId ? "disabled" : "primary"}
              title='บันทึก'
              onClick={() => setConfirmSubmit(true)}
              disabled={!isFormValid || !cusId}
            />
          </Col>
        </Row>
      </StyledForm>
      <ConfirmModal
        isInput={isEdit ? true : false}
        visible={confirmSubmit}
        onConfirm={() => {
          setConfirmSubmit(false);
          form.submit();
        }}
        disabled={beforeStatus === "APPROVED" && !reason}
        callBackInput={(val) => setReason(val)}
        onCancel={() => setConfirmSubmit(false)}
        title='ต้องการแก้ไขขอวงเงิน'
        desc='โปรดตรวจสอบรายละเอียดวงเงินอีกครั้งก่อนกดยืนยัน เพราะอาจส่งผลต่อการข้อมูลร้านค้าในระบบ'
      />
    </CardContainer>
  );
}

export default RequestCredit;
